import { createUserWithEmailAndPassword, sendEmailVerification, signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, signOut, type User as FirebaseUser } from "firebase/auth";
import { collection, doc, getDoc, getDocs, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from "./firebase";
import { prepareCentralAccess, accessRequest } from "./accessClient";
type Role = '최고관리자' | '새가족담당' | '재정담당' | '교육담당' | '일반';
const roleMap: Record<string, Role> = { master:'최고관리자', newcomer:'새가족담당', finance:'재정담당', education:'교육담당' };
async function createOrUpdateUser(user: FirebaseUser) {
  if (user.providerData.some((provider) => provider.providerId === 'password') && !user.emailVerified) {
    throw Object.assign(new Error('이메일 인증을 먼저 완료해 주세요.'), { code: 'auth/email-not-verified' });
  }
  if (await prepareCentralAccess(user)) return;
  const profile = doc(db, 'users', user.uid), permission = doc(db, 'user-permissions', user.uid);
  const snapshot = await getDoc(profile);
  if (snapshot.exists()) { await updateDoc(profile, { lastLoginAt: serverTimestamp() }); return; }
  await setDoc(profile, { email:user.email, displayName:user.displayName, role:'일반', isActive:true, createdAt:serverTimestamp(), lastLoginAt:serverTimestamp() });
  await setDoc(permission, { role:'일반', createdAt:serverTimestamp() });
}
async function getUserRole(uid: string): Promise<Role> {
  if (auth.currentUser?.uid !== uid) return '일반';
  try {
    if (await prepareCentralAccess(auth.currentUser)) {
      const response = await accessRequest('me'); if (!response.ok) return '일반';
      const access = await response.json();
      if (access.enabled !== true) return '일반';
      const grant = access.grants?.administration;
      return grant?.active === true ? roleMap[grant.role] || '일반' : '일반';
    }
    const [profile, permission] = await Promise.all([getDoc(doc(db,'users',uid)),getDoc(doc(db,'user-permissions',uid))]);
    return profile.data()?.isActive === true && profile.data()?.role === permission.data()?.role ? permission.data()?.role || '일반' : '일반';
  } catch { return '일반'; }
}
async function getAllUsers() {
  const snapshot = await getDocs(collection(db, 'users'));
  return snapshot.docs.map((item) => ({ uid:item.id, ...item.data(), createdAt:item.data().createdAt?.toDate(), lastLoginAt:item.data().lastLoginAt?.toDate() }));
}
const protectedChange = async () => { throw new Error('회원 관리의 서비스별 권한 탭을 사용해 주세요.'); };
export const authService = {
  signInWithGoogle: async () => {
    const provider = new GoogleAuthProvider();
    provider.addScope('email');
    return (await signInWithPopup(auth, provider)).user;
  },
  signInWithEmail: async (email: string, password: string) => {
    const { user } = await signInWithEmailAndPassword(auth, email.trim(), password);
    await user.reload();
    if (!user.emailVerified) {
      await signOut(auth);
      throw Object.assign(new Error('이메일 인증을 먼저 완료해 주세요.'), { code: 'auth/email-not-verified' });
    }
    return user;
  },
  signUpWithEmail: async (email: string, password: string) => {
    const { user } = await createUserWithEmailAndPassword(auth, email.trim(), password);
    try {
      await sendEmailVerification(user, { url: `${window.location.origin}/login`, handleCodeInApp: false });
    } finally {
      await signOut(auth);
    }
    return user.email || email.trim();
  },
  signOutUser: () => signOut(auth),
  onAuthChange: (callback: (user: FirebaseUser | null) => void) => auth.onAuthStateChanged(callback),
  getAllUsers, createOrUpdateUser, getUserRole,
  updateUserRole: protectedChange, toggleUserActive: protectedChange,
};
